// Oike · Mi marca — the collection "YouTube y LinkedIn" (explicación, palabras
// destacadas, idea o pregunta, podcast o entrevista).
//
// Same contract as the rest of brandRender.js: layoutSocial() is pure and decides
// every element of one piece in one format; drawPiece() paints it, so the
// catalog, the editor and the PNG are the same drawing. The composition adapts
// to who appears (Ale, guests or both) and how many: the people get smaller and
// the text column narrower, never over a face.
// Photos are only scaled uniformly and cropped: Ale's from the waist up
// (selfFrameRect), the others with their own crop (coverRect). A photo with a
// transparent background (a cut-out silhouette) is drawn as it is, with an
// optional halo around the silhouette; nothing inside the photo is changed.
import {
  BRAND_FORMATS, BRAND_TEMPLATES, LEGIBILITY_MIN, AA_NORMAL, AA_LARGE, contrastRatio, isLargeText, shownSize,
  coverRect, selfFrameRect, photoUsable, activePeople, highlightKey, SOCIAL_STYLE_DEFAULTS, isCarouselFormat,
} from './brandConfig.js';

const ASPECT = 0.8;                  // a waist-up photo place: 4 wide × 5 high
const TILTS = [-2.4, 1.8, -1.6, 2.2, -2, 1.5];
const FACE = { x: 0.2, y: 0.1, w: 0.6, h: 0.38 }; // where faces are, inside a photo place
const MIN_PHOTO_SHOWN = 48;          // CSS px: a photo place narrower than this at the usual size hides the face

const inter = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
const area = (r) => Math.max(0, r.w) * Math.max(0, r.h);
const clipTo = (r, W, H) => { const x = Math.max(0, r.x), y = Math.max(0, r.y); return { x, y, w: Math.min(W, r.x + r.w) - x, h: Math.min(H, r.y + r.h) - y }; };

/** Words of a headline with their highlight (a word counts when its key is in the list). */
export function headlineTokens(text, highlights = []) {
  const keys = new Set((highlights || []).map(highlightKey).filter(Boolean));
  return String(text || '').split(/\s+/).filter(Boolean).map(t => ({ t, hl: keys.has(highlightKey(t)) }));
}

/**
 * The biggest size at which the tokens fit the box, word by word (a word is never
 * cut: a word wider than the box at the smallest size is an overflow).
 * blocks: each run of words with the same highlight is a block with padding.
 */
export function fitRich(measure, tokens, { fontOf, family, weight, maxSize, minSize, w, h, lh = 1.08, upper = false, blocks = false, tilt = blocks }) {
  const toks = tokens.map(x => ({ ...x, t: upper ? x.t.toUpperCase() : x.t }));
  let size = maxSize;
  for (;;) {
    const font = fontOf(family, size, weight);
    const space = measure(' ', font);
    const padX = blocks ? size * 0.26 : 0, gap = blocks ? size * 0.16 : space;
    const runsOf = (line) => {
      const runs = [];
      for (const tk of line) {
        const last = runs[runs.length - 1];
        if (last && last.hl === tk.hl) last.text += ` ${tk.t}`; else runs.push({ text: tk.t, hl: tk.hl });
      }
      let dx = 0;
      for (const r of runs) { r.tw = measure(r.text, font); r.w = r.tw + 2 * padX; r.dx = dx; dx += r.w + gap; }
      return { runs, width: Math.max(0, dx - gap) };
    };
    const lines = [];
    let cur = [];
    let overflow = false;
    for (const tk of toks) {
      const next = [...cur, tk];
      if (cur.length && runsOf(next).width > w) { lines.push(cur); cur = [tk]; } else cur = next;
      if (runsOf([tk]).width > w) overflow = true;
    }
    if (cur.length) lines.push(cur);
    const laid = lines.map(runsOf);
    const lineH = blocks ? size * (1 + 0.3) : size * lh;
    const pitch = blocks ? lineH + size * 0.2 : lineH;
    // A tilted block grows a little in height: room for it
    const extra = blocks && tilt ? Math.max(...laid.map(l => l.width), 0) * Math.sin(2.4 * Math.PI / 180) : 0;
    const height = lines.length ? (lines.length - 1) * pitch + lineH + extra : 0;
    if ((height <= h && !overflow) || size <= minSize) {
      return { size, font, lines: laid, lineH, pitch, height, padX, overflow: overflow || height > h + 0.5, extra };
    }
    size = Math.max(minSize, size - Math.max(1, Math.round(size * 0.04)));
  }
}

/**
 * Every element of a piece of the collection in one format.
 * piece.content: { slots, cast, guests, people: { [key]: { name, role, show } }, style }
 * piece.photos: { [key]: { photoId, crop, frame } }
 * images: { [key]: { w, h, photo, cutout } }
 */
export function layoutSocial(piece, { brand, measure, images = {}, slide = 0 }, { fitText, fontOf }) {
  const tpl = BRAND_TEMPLATES[piece.template];
  const fmt = BRAND_FORMATS[piece.format];
  const W = fmt.width, H = fmt.height, M = fmt.margin;
  const pal = brand.palette;
  const C = { bg: pal.forest, text: pal.ivory, accent: pal.mustard, panel: pal.chocolate };
  const fonts = brand.fonts || { body: 'Arial' };
  const content = piece.content || {};
  const style = { ...SOCIAL_STYLE_DEFAULTS, ...(content.style || {}) };
  const slot = (k) => String((typeof content.slots?.[k] === 'string' ? content.slots[k] : content.slots?.[k]?.text) || '').trim();
  const orient = W > H * 1.2 ? 'landscape' : H > W * 1.1 ? 'portrait' : 'square';
  const scale = W / fmt.viewWidth;   // export px per CSS px at the usual size
  const minPx = (role) => Math.ceil(LEGIBILITY_MIN[role] * scale);
  const ops = [{ type: 'rect', x: 0, y: 0, w: W, h: H, color: C.bg }];
  const checks = [];
  const textRects = [];
  const faces = [];
  const keepOut = fmt.cover || [];

  const contrast = (element, fg, bg, size, bold) => {
    const large = isLargeText(size, piece.format, bold);
    const ratio = contrastRatio(fg, bg);
    checks.push({ kind: 'contrast', element, fg, bg, ratio, needed: large ? AA_LARGE : AA_NORMAL, ok: ratio >= (large ? AA_LARGE : AA_NORMAL) });
  };
  const legible = (element, role, size) => {
    const shown = shownSize(size, piece.format);
    const min = LEGIBILITY_MIN[role];
    checks.push({ kind: 'legibility', element, shown, min, ok: shown >= min });
  };

  /** A plain text (kicker, chips, names). */
  const text = (role, value, box, o = {}) => {
    const v = String(value || '').trim();
    if (!v) return 0;
    const weight = o.weight || 400;
    const r = fitText(measure, o.upper ? v.toUpperCase() : v, { family: o.family || fonts.body, weight, maxSize: o.max, minSize: o.min, w: box.w, h: box.h, lineHeight: o.lh || 1.15 });
    const bg = o.bg || C.bg;
    let fg = o.color || C.text;
    if (o.accent) fg = contrastRatio(C.accent, bg) >= (isLargeText(r.size, piece.format, weight >= 700) ? AA_LARGE : AA_NORMAL) ? C.accent : C.text;
    const y0 = o.alignBottom ? box.y + box.h - r.height : o.center ? box.y + (box.h - r.height) / 2 : box.y;
    const width = Math.max(0, ...r.lines.map(l => measure(l, r.font)));
    const align = o.align || 'left';
    ops.push({ type: 'text', role, element: o.element || role, lines: r.lines, font: r.font, size: r.size, lh: o.lh || 1.15, color: fg, x: box.x, y: y0, w: box.w, align });
    const tx = align === 'center' ? box.x + (box.w - width) / 2 : align === 'right' ? box.x + box.w - width : box.x;
    if (!o.inTag) textRects.push({ x: tx, y: y0, w: width, h: r.height, element: o.element || role });
    contrast(o.element || role, fg, bg, r.size, weight >= 700);
    legible(o.element || role, role === 'kicker' || role === 'small' ? 'small' : 'body', r.size);
    if (r.overflow) checks.push({ kind: 'overflow', element: o.element || role, ok: false });
    return { h: r.height, w: width, size: r.size, font: r.font, lines: r.lines };
  };

  /** The headline: highlighted words in mustard (text, blocks or underline). */
  const headline = (box, o) => {
    const v = o.text ?? slot('headline');
    if (!v) return { h: 0, w: 0 };
    let mode = o.mode || 'color';
    // blocks: ivory and mustard, slightly tilted; solid: straight mustard blocks with chocolate text
    const solid = mode === 'solid';
    const blocks = mode === 'blocks' || solid;
    const r = fitRich(measure, headlineTokens(v, style.highlights), {
      fontOf, family: 'Montserrat', weight: o.weight || 800, maxSize: o.max, minSize: Math.max(o.min, minPx('headline')),
      w: box.w, h: box.h, lh: o.lh || 1.06, upper: !!o.upper, blocks, tilt: !solid,
    });
    // Mustard words only where they pass for their size; otherwise ivory, underlined in mustard
    if (mode === 'color' && contrastRatio(C.accent, C.bg) < (isLargeText(r.size, piece.format, true) ? AA_LARGE : AA_NORMAL)) mode = 'underline';
    const y0 = o.valign === 'top' ? box.y : o.valign === 'bottom' ? box.y + box.h - r.height : box.y + (box.h - r.height) / 2;
    const lines = r.lines.map((l, i) => {
      const x = style.align === 'center' ? box.x + (box.w - l.width) / 2 : style.align === 'right' ? box.x + box.w - l.width : box.x;
      const y = y0 + r.extra / 2 + i * r.pitch;
      const runs = l.runs.map(run => {
        if (blocks) {
          const bg = solid ? (run.hl ? C.text : C.accent) : (run.hl ? C.accent : C.text);
          const fg = solid ? (run.hl ? C.bg : C.panel) : (run.hl ? C.panel : C.bg);
          return { text: run.text, dx: run.dx, w: run.w, tw: run.tw, color: fg, bg };
        }
        const fg = run.hl && mode === 'color' ? C.accent : C.text;
        return { text: run.text, dx: run.dx, w: run.w, tw: run.tw, color: fg, underline: run.hl && mode === 'underline' ? C.accent : null };
      });
      return { x, y, w: l.width, h: r.lineH, angle: blocks && !solid ? TILTS[i % TILTS.length] : 0, runs };
    });
    ops.push({ type: 'rich', element: o.element || 'Título', font: r.font, size: r.size, padX: r.padX, radius: solid ? 0 : Math.round(r.size * 0.1), lines });
    for (const l of lines) textRects.push({ x: l.x, y: l.y - (blocks ? l.w * 0.03 : 0), w: l.w, h: l.h + (blocks ? l.w * 0.06 : 0), element: o.element || 'Título' });
    const pairs = new Set();
    for (const l of lines) for (const run of l.runs) {
      const key = `${run.color}|${run.bg || C.bg}`;
      if (pairs.has(key)) continue;
      pairs.add(key);
      contrast(run.bg ? `${o.element || 'Título'} (bloque)` : run.color === C.accent ? 'Palabras destacadas' : (o.element || 'Título'), run.color, run.bg || C.bg, r.size, true);
    }
    legible(o.element || 'Título', 'headline', r.size);
    if (r.overflow) checks.push({ kind: 'overflow', element: o.element || 'Título', ok: false });
    return { h: r.height, w: Math.max(0, ...lines.map(l => l.w)), size: r.size, y: y0, lines };
  };


  const t = piece.template;
  const MODES = { highlight: 'blocks', blocks: 'solid', idea: 'underline' };
  const UPPER = { highlight: true, blocks: true };
  /** A small hand-drawn detail (ivory line), only where it touches no text and no face. */
  // candidates: places in order of preference; near: if none is free, the free place closest to this point
  const doodle = (kind, size, candidates, avoid, near = null) => {
    const sz = Math.round(size);
    const free = ([x, y]) => {
      const r = { x: Math.round(x), y: Math.round(y), w: sz, h: sz };
      if (r.x < M * 0.25 || r.y < M * 0.25 || r.x + r.w > W - M * 0.25 || r.y + r.h > H - M * 0.25) return null;
      return avoid.some(a => inter(a, r)) || keepOut.some(c => inter(c, r)) ? null : r;
    };
    let r = candidates.map(free).find(Boolean) || null;
    if (!r && near) {
      const step = Math.max(12, Math.round(sz / 4));
      let best = null, bd = Infinity;
      for (let y = 0; y <= H - sz; y += step) for (let x = 0; x <= W - sz; x += step) {
        const d = (x - near[0]) ** 2 + (y - near[1]) ** 2;
        if (d < bd) { const f = free([x, y]); if (f) { best = f; bd = d; } }
      }
      r = best;
    }
    if (r) ops.push({ type: 'doodle', kind, ...r, color: C.text, width: Math.max(3, Math.round(size * 0.035)), decor: true });
    return r;
  };

  // ── Carousel (Instagram): the slides after the cover are text, in the same style ──
  if (isCarouselFormat(piece.format) && slide >= 1) {
    const slides = Array.isArray(content.slides) ? content.slides : [];
    const i = Math.min(slide, slides.length) - 1;
    const cur = slides[i] || {};
    const val = (k) => String((typeof cur.slots?.[k] === 'string' ? cur.slots[k] : cur.slots?.[k]?.text) || '').trim();
    const total = slides.length + 1;
    const last = slide >= total - 1;
    const decorOn = style.decor !== false;
    text('small', `${String(slide + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`, { x: W - M - 260, y: M, w: 260, h: minPx('small') * 1.4 }, { max: Math.round(minPx('small') * 1.15), min: minPx('small'), weight: 700, align: 'right', accent: true, element: 'Número de lámina' });
    let y = M + Math.round(H * 0.1);
    if (decorOn) {
      if (t === 'blocks') doodle('sparkle', Math.round(W * 0.09), [[M, M - 10]], textRects);
      else ops.push({ type: 'rect', x: M, y: y - 40, w: Math.round(W * 0.09), h: 10, color: C.accent, decor: true });
    }
    const bottom = H - M - (last ? 90 : 0);
    const hl = headline({ x: M, y, w: W - 2 * M, h: Math.round(H * 0.4) }, { text: val('headline'), max: 100, min: 56, mode: MODES[t] || 'color', upper: !!UPPER[t], weight: t === 'idea' || t === 'blocks' ? 900 : 800, valign: 'top', element: 'Título de la lámina' });
    y += hl.h + Math.round(M * 0.6);
    if (val('body')) text('body', val('body'), { x: M, y, w: W - 2 * M, h: Math.max(80, bottom - y) }, { max: 46, min: minPx('body'), lh: 1.4, align: style.align, element: 'Texto de la lámina' });
    if (last) {
      ops.push({ type: 'rect', x: M, y: H - M - 34, w: 16, h: 16, color: C.accent });
      text('small', brand.name || '', { x: M + 32, y: H - M - 48, w: W - 2 * M - 32, h: 44 }, { max: Math.round(minPx('small') * 1.15), min: minPx('small'), weight: 700, element: 'Firma' });
    } else if (decorOn) doodle('arrow', Math.round(W * 0.1), [[W - M - W * 0.1, H - M - W * 0.1]], textRects);
    for (const r of textRects) if (r.x < -1 || r.y < -1 || r.x + r.w > W + 1 || r.y + r.h > H + 1) checks.push({ kind: 'overflow', element: r.element, ok: false });
    return { ops, checks, width: W, height: H, faces, textRects };
  }

  // ── People: who appears, and where ──
  const keys = activePeople(content);
  const n = keys.length;
  const people = keys.map(key => {
    const def = tpl.photos.find(p => p.key === key);
    const p = piece.photos?.[key];
    const img = p?.photoId ? images[key] : null;
    const info = content.people?.[key] || {};
    const fromPhoto = img?.photo || {};
    return {
      key, label: def?.label || key, self: !!def?.self, placed: p || null, img,
      name: String(info.name || (def?.self ? brand.name : fromPhoto.personName) || '').trim(),
      role: String(info.role || (def?.self ? '' : fromPhoto.personRole) || '').trim(),
      show: info.show ?? piece.template === 'podcast',
    };
  });
  const allCutout = people.every(p => p.img?.cutout);
  const overlap = allCutout ? 0.12 : -0.03;
  const side = style.side === 'left' ? 'left' : 'right';

  /** The places of the people in an area, bottom-aligned, as big as fits (same size each). */
  const placePeople = (a, { maxH = a.h, bleed = 0, anchor = side } = {}) => {
    const units = n - (n - 1) * overlap;
    let h = Math.min(maxH, a.h, (a.w + bleed) / units / ASPECT);
    const w = h * ASPECT;
    const total = w * units;
    let x0 = anchor === 'center' ? a.x + (a.w - total) / 2 : anchor === 'left' ? a.x - Math.min(bleed, Math.max(0, total - a.w)) : a.x + a.w - total + Math.min(bleed, Math.max(0, total - a.w));
    if (anchor === 'left' && total <= a.w) x0 = a.x;
    if (anchor !== 'left' && anchor !== 'center' && total <= a.w) x0 = a.x + a.w - total;
    // Faces never in what the channel crops at the sides (Instagram's profile grid): the group moves inward
    const faceX0 = x0 + w * FACE.x, faceX1 = x0 + (total - w) + w * (FACE.x + FACE.w);
    for (const c of keepOut) {
      if (c.h < H * 0.9) continue;
      if (c.x <= 0 && faceX0 < c.x + c.w + 4) x0 += c.x + c.w + 4 - faceX0;
      else if (c.x > 0 && faceX1 > c.x - 4) x0 -= faceX1 - (c.x - 4);
    }
    return people.map((p, i) => ({ ...p, box: { x: Math.round(x0 + i * w * (1 - overlap)), y: Math.round(a.y + a.h - h), w: Math.round(w), h: Math.round(h) } }));
  };

  // ── Composition by template, format and number of people ──
  const gap = Math.round(M * 0.5);
  let textBox, peopleArea, placeOpts = {}, stacked = false;
  // Podcast and bloques mostaza keep the title on top in vertical and square formats (big blocks need the width)
  if (orient === 'landscape' || (n === 1 && t !== 'podcast' && t !== 'blocks')) {
    // Side by side: the people on one side, the text on the other
    const share = orient === 'landscape' ? (n === 1 ? (t === 'idea' ? 0.4 : 0.44) : n === 2 ? 0.54 : 0.62) : (t === 'idea' ? 0.46 : 0.5);
    const pw = Math.round(W * share);
    const top = orient === 'landscape' ? Math.round(M * 0.5) : Math.round(H * (orient === 'portrait' ? 0.3 : 0.22));
    peopleArea = side === 'right' ? { x: W - pw, y: top, w: pw, h: H - top } : { x: 0, y: top, w: pw, h: H - top };
    const tw = W - pw - M - gap;
    textBox = side === 'right' ? { x: M, y: M, w: tw, h: H - 2 * M } : { x: pw + gap, y: M, w: tw, h: H - 2 * M };
    // One person on LinkedIn: taller than its column, bleeding off the outer edge (the face stays inside)
    if (orient !== 'landscape') placeOpts = { bleed: Math.round(pw * 0.2), maxH: Math.round(H * (orient === 'portrait' ? 0.6 : 0.74)) };
  } else {
    // Text on top, the people in a row below
    const textH = Math.round(H * (orient === 'portrait' ? (n > 2 ? 0.42 : 0.4) : (n > 2 ? 0.4 : 0.36)));
    textBox = { x: M, y: M, w: W - 2 * M, h: textH - M };
    const top = textH + Math.round(M * 0.4);
    stacked = true;
    peopleArea = { x: Math.round(M * 0.4), y: top, w: W - Math.round(M * 0.8), h: H - top };
    placeOpts = { anchor: n === 1 ? side : 'center' };
  }
  // The channel's covered corner (YouTube: duration) never gets text
  for (const c of keepOut) {
    if (inter(textBox, c)) textBox = { ...textBox, h: Math.max(80, Math.min(textBox.h, c.y - 12 - textBox.y)) };
  }
  const placed = placePeople(peopleArea, placeOpts);

  // ── Details behind the people (optional) ──
  const decor = style.decor !== false;
  const behind = [];
  if (decor && placed.length) {
    const first = placed[0].box, last = placed[placed.length - 1].box;
    const groupBox = { x: first.x, y: Math.min(...placed.map(p => p.box.y)), w: last.x + last.w - first.x, h: Math.max(...placed.map(p => p.box.h)) };
    if (t === 'explainer') {
      // A grid of mustard dots in the upper outer corner of the people's side
      const step = Math.round(Math.max(18, W * 0.024)), rr = Math.max(4, Math.round(step * 0.22));
      const gx = side === 'right' ? W - M - step * 4 : M, gy = Math.max(M, groupBox.y - step * 1.5);
      for (let i = 0; i < 5; i++) for (let j = 0; j < 4; j++) behind.push({ type: 'rect', x: gx + j * step, y: gy + i * step, w: rr * 2, h: rr * 2, radius: rr, color: C.accent, decor: true });
    } else if (t === 'idea') {
      // A mustard circle behind the people, around the faces
      const r = Math.round(Math.min(groupBox.w, groupBox.h) * (n === 1 ? 0.46 : 0.4));
      behind.push({ type: 'rect', x: Math.round(groupBox.x + groupBox.w / 2 - r), y: Math.round(groupBox.y + groupBox.h * 0.36 - r), w: 2 * r, h: 2 * r, radius: r, color: C.accent, decor: true });
    } else if (t === 'podcast') {
      const r = Math.round(Math.min(groupBox.w, groupBox.h) * 0.45);
      behind.push({ type: 'ring', cx: Math.round(groupBox.x + groupBox.w / 2), cy: Math.round(groupBox.y + groupBox.h * 0.38), r, color: C.accent, width: Math.max(6, Math.round(W * 0.007)), decor: true });
    }
  }
  ops.push(...behind);

  // ── The people (guests first, Ale on top when they overlap) ──
  const halo = style.halo !== false ? { color: style.haloColor === 'mustard' ? C.accent : C.text, width: Math.max(6, Math.round(W * 0.007)) } : null;
  const order = [...placed].sort((a, b) => (a.self ? 1 : 0) - (b.self ? 1 : 0));
  const clipRegion = { x: 0, y: 0, w: W, h: H };
  for (const p of order) {
    const box = p.box;
    if (p.img) {
      const img = { w: p.img.w, h: p.img.h };
      let rect, frame = box;
      if (p.self) {
        const r = selfFrameRect(img, box, p.placed.frame, p.placed.crop);
        rect = r.rect;
        if (!p.img.cutout) frame = r.box;
      } else rect = coverRect(img, box, p.placed.crop);
      const cutout = !!p.img.cutout;
      ops.push({ type: 'person', slot: p.key, box: frame, rect, cutout, clip: cutout ? clipRegion : frame, radius: cutout ? 0 : Math.round(frame.w * 0.05), halo });
      const u = photoUsable(p.img.photo);
      if (!u.ok) checks.push({ kind: 'photo', element: p.label, reason: u.reason, ok: false });
    } else {
      if (p.placed?.photoId) checks.push({ kind: 'photo', element: p.label, reason: 'loading_or_missing', ok: false });
      ops.push({ type: 'placeholder', slot: p.key, box, circle: false, label: `Falta la foto: ${p.label}`, color: C.text, bg: C.bg });
      if (!p.placed?.photoId) checks.push({ kind: 'photo_needed', element: p.label, ok: false });
    }
    const face = { x: box.x + box.w * FACE.x, y: box.y + box.h * FACE.y, w: box.w * FACE.w, h: box.h * FACE.h, element: p.label };
    faces.push(face);
    if (box.w / scale < MIN_PHOTO_SHOWN) checks.push({ kind: 'photo_small', element: p.label, shown: Math.round(box.w / scale), ok: false });
    if (area(clipTo(face, W, H)) < area(face) * 0.9) checks.push({ kind: 'face_cut', element: p.label, ok: false });
  }

  // ── Texts ──
  const textOps = ops.length, textRectsFrom = textRects.length;
  const align = style.align;
  let y = textBox.y;
  const block = { ...textBox };
  // Kicker (and for the podcast, the program and episode as chips)
  if (t === 'podcast') {
    const show = slot('kicker'), ep = slot('episode');
    if (show || ep) {
      // The program (mustard chip) and the episode (outlined chip): each on one line if it
      // fits, otherwise on two; a chip that does not fit next to another goes to the next row
      const size = Math.round(minPx('small') * 1.25);
      const pad = Math.round(size * 0.45), space = Math.round(size * 0.5);
      const wavesW = decor ? Math.round(size * 1.6) : 0;
      const items = [show && { text: show.toUpperCase(), fill: C.accent, fg: C.panel, element: 'Programa' }, ep && { text: ep.toUpperCase(), fill: null, fg: C.text, element: 'Episodio' }].filter(Boolean)
        .map((it, i) => {
          const r = fitText(measure, it.text, { family: fonts.body, weight: 700, maxSize: size, minSize: minPx('small'), w: textBox.w - 2 * pad - (i === 0 ? wavesW : 0), h: size * 1.15 * 2, lineHeight: 1.15 });
          const tw = Math.max(...r.lines.map(l => measure(l, r.font)));
          return { ...it, r, w: Math.ceil(tw) + 2 * pad, h: Math.ceil(r.height) + 2 * pad };
        });
      const rows = [[]];
      let used = wavesW;
      for (const it of items) {
        if (rows[rows.length - 1].length && used + it.w > textBox.w) { rows.push([]); used = 0; }
        rows[rows.length - 1].push(it); used += it.w + space;
      }
      rows.forEach((row, ri) => {
        const rowW = row.reduce((sum, it) => sum + it.w + space, -space) + (ri === 0 ? wavesW : 0);
        const rowH = Math.max(...row.map(it => it.h));
        let cx = align === 'center' ? textBox.x + (textBox.w - rowW) / 2 : align === 'right' ? textBox.x + textBox.w - rowW : textBox.x;
        if (ri === 0 && decor) {
          // Sound waves: five mustard bars
          const bw = Math.max(5, Math.round(size * 0.18));
          [0.45, 0.9, 1.3, 0.8, 0.5].forEach((k, i) => { const bh = Math.round(size * k); ops.push({ type: 'rect', x: Math.round(cx + i * bw * 1.7), y: y + Math.round((rowH - bh) / 2), w: bw, h: bh, radius: bw / 2, color: C.accent, decor: true }); });
          cx += wavesW;
        }
        for (const it of row) {
          const radius = Math.min(it.h / 2, size * 0.8);
          if (it.fill) ops.push({ type: 'rect', x: Math.round(cx), y, w: it.w, h: it.h, radius, color: it.fill });
          else ops.push({ type: 'frame', x: Math.round(cx), y, w: it.w, h: it.h, radius, color: C.accent, width: Math.max(3, Math.round(size * 0.08)) });
          text('small', it.text, { x: cx + pad, y: y + pad, w: it.w - 2 * pad + 1, h: it.h - 2 * pad + 1 }, { max: it.r.size, min: it.r.size, weight: 700, color: it.fg, bg: it.fill || C.bg, element: it.element });
          cx += it.w + space;
        }
        y += rowH + Math.round(size * (ri === rows.length - 1 ? 0.9 : 0.35));
      });
    }
  } else if (slot('kicker')) {
    const k = text('kicker', slot('kicker'), { x: textBox.x, y, w: textBox.w, h: minPx('small') * 1.4 * 2.3 }, { max: Math.round(minPx('small') * (t === 'blocks' ? 1.45 : 1.3)), min: minPx('small'), weight: t === 'blocks' ? 800 : 700, family: t === 'blocks' ? 'Montserrat' : undefined, upper: true, accent: t !== 'blocks', align, element: 'Antetítulo' });
    y += k.h + Math.round(M * 0.35);
  }
  // A mustard bar marks the start of the headline (explanation)
  if (decor && t === 'explainer') {
    const bw = Math.round(W * 0.075), bh = Math.max(8, Math.round(W * 0.008));
    const bx = align === 'center' ? textBox.x + (textBox.w - bw) / 2 : align === 'right' ? textBox.x + textBox.w - bw : textBox.x;
    ops.push({ type: 'rect', x: Math.round(bx), y, w: bw, h: bh, color: C.accent, decor: true });
    y += bh + Math.round(M * 0.4);
  }
  if (decor && t === 'highlight') y += Math.round(M * 0.35);
  // Names under the headline space (only where shown)
  const tagged = placed.filter(p => p.show && (p.name || p.role));
  const hbox = { x: block.x, y, w: block.w, h: block.y + block.h - y };
  const sizes = {
    explainer: orient === 'landscape' ? [n > 1 ? 104 : 118, 64] : [104, 52],
    highlight: orient === 'landscape' ? [n > 1 ? 82 : 92, 64] : [88, 50],
    idea: orient === 'landscape' ? [n > 1 ? 118 : 132, 64] : [126, 54],
    podcast: orient === 'landscape' ? [n > 1 ? 92 : 104, 64] : [96, 50],
    blocks: orient === 'landscape' ? [n > 1 ? 100 : 124, 64] : [116, 54],
  }[t];
  const hl = headline(hbox, {
    max: sizes[0], min: sizes[1], mode: MODES[t] || 'color', upper: !!UPPER[t],
    weight: t === 'idea' || t === 'blocks' ? 900 : 800, valign: t === 'podcast' || (orient !== 'landscape' && n > 1) ? 'top' : 'center', element: t === 'idea' ? 'Idea o pregunta' : 'Título',
  });
  // Strokes next to the first block (palabras destacadas)
  if (decor && t === 'highlight' && hl.lines?.length) {
    const l0 = hl.lines[0];
    const ox = align === 'right' ? l0.x + l0.w + 10 : l0.x - 10, oy = l0.y - 8, len = Math.round(hl.size * 0.42), wdt = Math.max(5, Math.round(hl.size * 0.07));
    const dir = align === 'right' ? 1 : -1;
    for (const [ux, uy] of [[1, -0.15], [0.72, -0.72], [0.15, -1]]) {
      ops.push({ type: 'line', x1: ox, y1: oy, x2: Math.round(ox + dir * ux * len), y2: Math.round(oy + uy * len), color: C.accent, width: wdt, decor: true });
    }
  }
  // A big sign (question mark or quotes) in a free corner (idea)
  if (decor && t === 'idea') {
    const glyph = /\?\s*$/.test(slot('headline')) ? '?' : '“';
    const gs = Math.round(Math.min(W, H) * (orient === 'landscape' ? 0.3 : 0.24));
    const gw = measure(glyph, fontOf('Montserrat', gs, 900));
    const gx = side === 'right' ? peopleArea.x - gw * 0.15 : peopleArea.x + peopleArea.w - gw * 0.85;
    const g = { x: Math.round(gx), y: Math.round(M * 0.35), w: Math.round(gw), h: Math.round(gs * 0.95) };
    if (!faces.some(f => inter(f, g)) && !textRects.some(r => inter(r, g))) ops.push({ type: 'text', role: 'decor', decor: true, element: 'Detalle gráfico', lines: [glyph], font: fontOf('Montserrat', gs, 900), size: gs, lh: 1, color: C.accent, x: g.x, y: g.y, w: g.w, align: 'left' });
  }

  // Text on top of the people: centered in the free space above them, never a gap under a short title
  if (stacked && placed.length) {
    const mine = textRects.slice(textRectsFrom);
    const bottom = Math.max(textBox.y, ...mine.map(r => r.y + r.h));
    const shift = Math.round((Math.min(...placed.map(p => p.box.y)) - Math.round(M * 0.5) - bottom) / 2);
    if (shift > 0) {
      for (const o of ops.slice(textOps)) {
        if ((o.type === 'text' && o.decor) || o.type === 'doodle') continue;
        if (o.type === 'rich') o.lines.forEach(l => { l.y += shift; });
        else if (o.type === 'line') { o.y1 += shift; o.y2 += shift; }
        else if (typeof o.y === 'number') o.y += shift;
      }
      for (const r of mine) r.y += shift;
    }
  }

  // Small drawings (bloques mostaza): a light bulb next to the faces and a thought cloud in a free corner
  if (decor && t === 'blocks' && placed.length) {
    const top = Math.min(...placed.map(p => p.box.y));
    const s1 = Math.round(Math.min(W, H) * (orient === 'landscape' ? 0.2 : 0.15));
    const s2 = Math.round(s1 * 0.85);
    const inner = side === 'right' ? peopleArea.x : peopleArea.x + peopleArea.w;
    const outer = side === 'right' ? W - M * 0.6 : M * 0.6;
    const dir = side === 'right' ? 1 : -1;
    // Away from every photo (head and body), not only the face
    const avoid = [...faces, ...textRects, ...placed.map(p => ({ x: p.box.x, y: p.box.y, w: p.box.w, h: p.box.h }))];
    const bulbAt = [[inner + dir * 10 - (dir < 0 ? s1 : 0), Math.max(M * 0.4, top - s1 * 0.2)], [inner + dir * 10 - (dir < 0 ? s1 : 0), M * 0.4], [inner - dir * s1 * 0.6 - (dir < 0 ? s1 : 0), M * 0.4]];
    const f0 = faces[0] || { x: inner, y: top, w: 0, h: 0 };
    const bulb = doodle('bulb', s1, bulbAt, avoid, [dir > 0 ? f0.x - s1 : f0.x + f0.w, f0.y - s1 * 0.3]);
    const cloudAt = [[outer - (dir > 0 ? s2 : 0), M * 0.5], [outer - (dir > 0 ? s2 : 0), Math.max(M * 0.5, top - s2 * 1.05)]];
    doodle('cloud', s2, cloudAt, [...avoid, ...(bulb ? [{ x: bulb.x - 20, y: bulb.y - 20, w: bulb.w + 40, h: bulb.h + 40 }] : [])], [dir > 0 ? W - M - s2 : M, M]);
  }

  // ── Name and role tags, on the lower part of each photo (never over the face) ──
  for (const p of tagged) {
    const b = p.box;
    const nameSize = Math.round(minPx('small') * 1.2), roleSize = minPx('small');
    const pad = Math.round(nameSize * 0.45);
    const inner = Math.max(40, Math.round(b.w * (1 - Math.max(0, overlap)) - 16)) - 2 * pad;
    // The texts first (up to two lines each), then the tag around them
    const at = ops.length;
    let tw = 0, yy = 0;
    const parts = [];
    if (p.name) { const r = text('small', p.name, { x: 0, y: 0, w: inner, h: nameSize * 1.25 * 2 }, { max: nameSize, min: minPx('small'), weight: 700, bg: C.panel, inTag: true, lh: 1.25, element: `Nombre de ${p.label}` }); parts.push([ops[ops.length - 1], yy]); yy += r.h; tw = Math.max(tw, r.w); }
    if (p.role) { const r = text('small', p.role, { x: 0, y: 0, w: inner, h: roleSize * 1.25 * 2 }, { max: roleSize, min: minPx('small'), bg: C.panel, inTag: true, lh: 1.25, element: `Rol de ${p.label}` }); parts.push([ops[ops.length - 1], yy]); yy += r.h; tw = Math.max(tw, r.w); }
    // A name or role is never cut in the middle of a word: that is a text that does not fit
    for (const [v, f] of [[p.name, fontOf(fonts.body, minPx('small'), 700)], [p.role, fontOf(fonts.body, minPx('small'), 400)]]) {
      if (v && String(v).split(/\s+/).some(word => measure(word, f) > inner)) checks.push({ kind: 'overflow', element: `Etiqueta de ${p.label}`, ok: false });
    }
    const tagW = Math.round(tw + 2 * pad), tagH = Math.round(yy + 2 * pad);
    let tx = Math.round(b.x + Math.max(8, (b.w * (1 - Math.max(0, overlap)) - tagW) / 2));
    tx = Math.min(W - tagW - 8, Math.max(8, tx));
    let ty = Math.min(Math.round(b.y + b.h * 0.64), H - tagH - 12);
    // What the channel covers (YouTube: the duration) and the faces: the first place that avoids both
    // (where it is, moved to the side, moved up, or both)
    const free = (r) => !keepOut.some(c => inter(r, c)) && !faces.some(f => inter(r, f));
    const sideX = Math.max(8, Math.min(tx, ...keepOut.map(c => c.x - tagW - 10)));
    const upY = Math.min(ty, ...keepOut.map(c => c.y - tagH - 10));
    const best = [[tx, ty], [sideX, ty], [tx, upY], [sideX, upY]].find(([cx, cy]) => free({ x: cx, y: cy, w: tagW, h: tagH }));
    if (best) [tx, ty] = best; else if (keepOut.some(c => inter({ x: tx, y: ty, w: tagW, h: tagH }, c))) [tx, ty] = [sideX, ty];
    const tag = { x: tx, y: ty, w: tagW, h: tagH };
    ops.splice(at, 0, { type: 'rect', ...tag, radius: Math.round(nameSize * 0.3), color: C.panel, element: `Etiqueta de ${p.name || p.label}` });
    for (const [op, dy] of parts) { op.x = tx + pad; op.y = ty + pad + dy; op.w = tw + 1; }
    textRects.push({ ...tag, element: `Etiqueta de ${p.label}` });
  }

  // ── Checks: faces visible (no text over them, not under the channel's controls) ──
  for (const f of faces) {
    if (textRects.some(r => inter(r, f))) checks.push({ kind: 'face_covered', element: f.element, ok: false });
    if (keepOut.some(c => inter(c, f))) checks.push({ kind: 'face_covered', element: f.element, ok: false });
  }
  for (const r of textRects) if (r.x < -1 || r.y < -1 || r.x + r.w > W + 1 || r.y + r.h > H + 1) checks.push({ kind: 'overflow', element: r.element, ok: false });
  return { ops, checks, width: W, height: H, faces, textRects };
}
